import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Loading from "@/components/dashboard/Loading";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const empty = { title: "", department: "", location: "", job_type: "", experience: "", description: "", open: true };

export default function CareersAdmin() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const positions = useQuery({
    queryKey: ["admin-positions"],
    queryFn: async () => (await api.get("/careers/positions/manage")).data,
    retry: false,
  });
  const apps = useQuery({
    queryKey: ["admin-applications"],
    queryFn: async () => (await api.get("/careers/applications")).data,
    retry: false,
  });

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ title: p.title || "", department: p.department || "", location: p.location || "", job_type: p.job_type || "", experience: p.experience || "", description: p.description || "", open: !!p.open }); setDialogOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/careers/positions/${editing.id}`, form);
        toast.success("Position updated");
      } else {
        await api.post("/careers/positions", form);
        toast.success("Position created");
      }
      setDialogOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-positions"] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/careers/positions/${deleting.id}`);
      toast.success("Position deleted");
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ["admin-positions"] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  if (positions.isLoading) {
    return (<div><PageHeader title="Careers Manager" subtitle="Openings and applications." showDemo={false} /><Loading /></div>);
  }

  if (positions.error) {
    return (
      <div>
        <PageHeader title="Careers Manager" subtitle="Openings and applications." showDemo={false} />
        <Panel className="p-12 text-center">
          <p className="text-white font-medium">Admin access required</p>
          <p className="mt-1 text-sm text-slate-400">Only the Vvvoraa admin account can manage careers content.</p>
        </Panel>
      </div>
    );
  }

  const rows = positions.data || [];
  const appRows = apps.data || [];

  return (
    <div className="space-y-8">
      <PageHeader title="Careers Manager" subtitle={`${rows.length} opening${rows.length === 1 ? "" : "s"} · ${appRows.length} application${appRows.length === 1 ? "" : "s"}`} showDemo={false}>
        <Button onClick={openCreate} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="position-create-btn">
          <Plus className="h-4 w-4" /> New Opening
        </Button>
      </PageHeader>

      <Panel className="p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/8">
          <h2 className="text-sm font-semibold text-white">Openings shown on /careers</h2>
        </div>
        {rows.length === 0 ? (
          <p className="p-8 text-sm text-slate-400 text-center" data-testid="positions-empty">No openings yet. Create one to show it on the Careers page.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {rows.map((p, i) => (
              <div key={p.id || i} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`admin-position-${i}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white">{p.title}</p>
                    <span className={`text-[10px] font-mono uppercase tracking-wider rounded-full px-2 py-0.5 ${p.open ? "bg-emerald-500/12 text-emerald-400" : "bg-slate-500/12 text-slate-400"}`}>
                      {p.open ? "Open" : "Closed"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">{[p.department, p.location, p.job_type].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="border-white/15 bg-transparent text-white rounded-full hover:bg-white/5" data-testid={`position-edit-${i}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDeleting(p)} className="border-red-500/25 bg-transparent text-red-400 rounded-full hover:bg-red-500/10" data-testid={`position-delete-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel className="p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/8">
          <h2 className="text-sm font-semibold text-white">Applications</h2>
        </div>
        {appRows.length === 0 ? (
          <p className="p-8 text-sm text-slate-400 text-center" data-testid="applications-empty">No applications yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-white/8">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Interest / Position</th>
                  <th className="px-5 py-3 font-medium">Location</th>
                  <th className="px-5 py-3 font-medium">Duration</th>
                  <th className="px-5 py-3 font-medium">Applied</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {appRows.map((a, i) => (
                  <tr key={a.id || i} className="text-slate-300" data-testid={`application-row-${i}`}>
                    <td className="px-5 py-3 text-white">{a.full_name}</td>
                    <td className="px-5 py-3">{a.email}</td>
                    <td className="px-5 py-3">{a.interest_area || a.position}</td>
                    <td className="px-5 py-3">{a.location || "—"}</td>
                    <td className="px-5 py-3">{a.duration || "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0B1526] border-white/10 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit opening" : "New opening"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div className="sm:col-span-2">
              <Label className="text-slate-300">Title *</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1.5 bg-white/[0.03] border-white/12 text-white" data-testid="position-form-title" />
            </div>
            <div>
              <Label className="text-slate-300">Department</Label>
              <Input value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} className="mt-1.5 bg-white/[0.03] border-white/12 text-white" data-testid="position-form-department" />
            </div>
            <div>
              <Label className="text-slate-300">Location</Label>
              <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="mt-1.5 bg-white/[0.03] border-white/12 text-white" data-testid="position-form-location" />
            </div>
            <div>
              <Label className="text-slate-300">Job type</Label>
              <Input value={form.job_type} onChange={(e) => setForm((f) => ({ ...f, job_type: e.target.value }))} placeholder="e.g. Internship, Full-time" className="mt-1.5 bg-white/[0.03] border-white/12 text-white" data-testid="position-form-type" />
            </div>
            <div>
              <Label className="text-slate-300">Experience</Label>
              <Input value={form.experience} onChange={(e) => setForm((f) => ({ ...f, experience: e.target.value }))} className="mt-1.5 bg-white/[0.03] border-white/12 text-white" data-testid="position-form-experience" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1.5 bg-white/[0.03] border-white/12 text-white min-h-[90px]" data-testid="position-form-description" />
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={form.open} onChange={(e) => setForm((f) => ({ ...f, open: e.target.checked }))} className="h-4 w-4 rounded border-white/20 bg-white/5 accent-[#4F8CFF]" data-testid="position-form-open" />
              Visible on the Careers page
            </label>
          </div>
          <Button onClick={save} disabled={saving} className="mt-4 w-full bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="position-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create opening"}
          </Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[#0B1526] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this opening?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              "{deleting?.title}" will be removed from the Careers page permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5" data-testid="position-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-500 hover:bg-red-600 text-white" data-testid="position-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
